import {apolloClient} from '../apolloClient';
import {envIsIntegration} from '../config';
import {
  EntityFeatureFlagsDocument,
  type EntityFeatureFlagsQuery,
  type EntityFeatureFlagsQueryVariables,
  OrgType,
  RampIdType,
  useEntityFeatureFlagsQuery,
  useOrganizationFeatureFlagsQuery,
  useViewerFeatureFlagsQuery,
  useViewerOrganizationFeatureFlagsQuery,
  ViewerFeatureFlagsDocument,
  type ViewerFeatureFlagsQuery,
  type ViewerFeatureFlagsQueryVariables,
} from '../generated/graphql';
import {type ApolloClient} from '../state/types';
import {isAnonymousEntity} from '../state/workspaces/utils';
import {getDatadogRum} from './datadog/datadogRum';
import {useDatadogFeatureFlag} from './datadog/hooks';
import {propagateErrorsContext} from './errors';

export type RampFlagStatus = {
  isEnabled: boolean;
  loading: boolean;
};

// TODO: we should auto generate this list from ramp_settings.go's RampFeatures
// list. For now, we have to manually keep this list in sync.
// ALSO TODO: we should also have the server tell us the ramp key *type* so that the
// user just calls useRampFlag('disable-fuzzy-search') and we can figure out the type
// based on the RampSettings' IDType in ramp_settings.go
export type RampKey =
  | 'account-selector-new'
  | 'account-selector'
  | 'automation_execution_history'
  | 'backend_line_construction_entity'
  | 'backend_line_construction_org'
  | 'backend_line_construction_smoothing'
  | 'backend_line_construction_time_units'
  | 'backend_line_construction_user'
  | 'backend_smoothing_deployment'
  | 'backend_smoothing_entity'
  | 'backend_smoothing_user'
  | 'baseline_delta_metrics'
  | 'baseline_runs'
  | 'brsdq_without_run_info_entity'
  | 'brsdq_without_run_info_org'
  | 'brsdq_without_run_info_user'
  | 'bulk_org_invitations'
  | 'clientside_dynamic_timeouts_entity'
  | 'combined_metrics_runs_limit_ui'
  | 'coreweave_evals_public_queue'
  | 'coreweave_observability'
  | 'cross_project_pinned_runs'
  | 'disable-fuzzy-search'
  | 'disable-query-merging'
  | 'disable-update-history-key-info-org'
  | 'disable-update-history-key-info-user'
  | 'dynamic_bin_sizing_entity'
  | 'dynamic_bin_sizing_org'
  | 'dynamic_bin_sizing_user'
  | 'embedded-panels-entity'
  | 'embedded-panels-user'
  | 'enable-opfs-cache'
  | 'entity_less_profile_page'
  | 'fast_workspace_autosave'
  | 'force-full-fidelity-in-reports-entity'
  | 'force-full-fidelity-in-reports-user'
  | 'format-x-axis-entity'
  | 'format-x-axis-user'
  | 'full_fidelity_ranges_deployment'
  | 'full_fidelity_ranges_entity'
  | 'full_fidelity_ranges_user'
  | 'full_fidelity_run_per_chart_org'
  | 'full_fidelity_run_per_chart_user'
  | 'fullscreen_line_plot_editing_entity'
  | 'fullscreen_line_plot_editing_user'
  | 'global-registry'
  | 'gql-long-term-cache-entity'
  | 'gql-long-term-cache-user'
  | 'hide-summary-histograms'
  | 'image_zoom_pan_user'
  | 'inference_as_product'
  | 'inference-catalog'
  | 'issues-observability-entity'
  | 'issues-observability-org'
  | 'issues-observability-user'
  | 'line-construction-profiling'
  | 'line_plot_profiler'
  | 'link_weave_prompts_to_registry'
  | 'low-resolution-queries-entity' // To be deprecated, being migrated to statsig
  | 'low-resolution-queries-org' // To be deprecated, being migrated to statsig
  | 'low-resolution-queries-user' // To be deprecated, being migrated to statsig
  | 'media_browser_modal_enabled_entity'
  | 'media_browser_modal_enabled_user'
  | 'media_panel_compare_mode_entity'
  | 'media_panel_compare_mode_org'
  | 'media_panel_compare_mode_user'
  | 'media_panel_layout_refactor'
  | 'media_panel_tracking_debug_entity'
  | 'media_panel_tracking_debug_user'
  | 'media-panel-tracking-entity'
  | 'media-panel-tracking-org'
  | 'media-panel-tracking-user'
  | 'member-table-last-active-at'
  | 'metrics-search-api-entity'
  | 'metrics-search-api-user'
  | 'model-registry-migration-notification'
  | 'multi-runset-history-keys-entity'
  | 'multi-runset-history-keys-org'
  | 'multi-runset-history-keys-user'
  | 'no-keysets-org'
  | 'no-keysets-user'
  | 'non-monotonic-x-axis-entity'
  | 'non-monotonic-x-axis-org'
  | 'non-monotonic-x-axis-user'
  | 'org_alerts_tab'
  | 'org-level-service-accounts'
  | 'overlay-workspace-drawer-by-user'
  | 'pinned_runs'
  | 'pro_plan_monthly_option'
  | 'project-specific-roles'
  | 'react_concurrent_mode'
  | 'registry_homepage_adjustments'
  | 'registry_ui_phase_2'
  | 'registry_ui_phase_3'
  | 'registry-automations'
  | 'registry-observer-role'
  | 'registry-search'
  | 'registry-team-permissions'
  | 'run_logs_pagination'
  | 'run-metric-notifications-entity'
  | 'run-metric-notifications'
  | 'run-metric-zscore-entity'
  | 'saas_coreweave_storage_byob'
  | 'sampled-runs-line-plot-notifications'
  | 'semantic_legend_bucket_editing_org'
  | 'semantic-legend-entity'
  | 'semantic-legend-org'
  | 'semantic-legend-user'
  | 'show_org_activity_page'
  | 'smart_feature_settings'
  | 'source_artifact_protection'
  | 'starred_registries'
  | 'starred-projects'
  | 'team-readme-section'
  | 'unlimited_tracked_hours_for_all'
  | 'uplot_user'
  | 'use-backend-grouping-entity'
  | 'use-backend-grouping-org'
  | 'use-backend-grouping-user'
  | 'use-backend-smoothing-entity'
  | 'use-backend-smoothing-user'
  | 'weave_annotation_queue'
  | 'weave_panels_in_workspace'
  | 'workspace_column_editor_redesign_org'
  | 'workspace_column_editor_redesign_user'
  | 'video_streaming_entity'
  | 'enable_account_homepage';

export const useEntityRampFlagWithLoading = (
  entityName: string | undefined,
  rampKey: RampKey,
  rampIDType: RampIdType,
  getEntityFlags: typeof useEntityFeatureFlagsQuery = useEntityFeatureFlagsQuery
) => {
  const setFlag = useDatadogFeatureFlag(rampKey);
  const {data, loading} = getEntityFlags({
    variables: {teamName: entityName, rampIDType},
    skip: entityName == null || isAnonymousEntity(entityName),
  });
  // All features are enabled for CI, unless explicitly disabled in their own tests
  if (
    envIsIntegration &&
    // dynamic_bin_sizing_entity are disabled while in experimental mode since they would require redoing screenshots
    ![
      'backend_line_construction_entity',
      'backend_line_construction_user',
      'brsdq_without_run_info_entity',
      'dynamic_bin_sizing_entity',
      'low-resolution-queries-entity', // deprecated flag
      'media_browser_modal_enabled_entity',
      'media_panel_individual_loading_entity',
      'semantic-legend-entity',
      'semantic-legend-user',
    ].includes(rampKey)
  ) {
    setFlag(true);
    return {
      loading: false,
      data: true,
    };
  }
  const teamFlagsGlobalList = data?.entity?.featureFlags;
  if (!teamFlagsGlobalList) {
    setFlag(false);
    // disabling this until we have an actionable use for it. It's very noisy
    // console.warn('useEntityRampFlag: returning false due to no data');
    return {
      loading,
      data: false,
    };
  }

  const isEnabled =
    teamFlagsGlobalList.find(f => f?.rampKey === rampKey)?.isEnabled ?? false;
  setFlag(isEnabled);
  return {
    loading,
    data: isEnabled,
  };
};

export const useEntityRampFlag = (
  entityName: string | undefined,
  rampKey: RampKey,
  rampIDType: RampIdType,
  getEntityFlags: typeof useEntityFeatureFlagsQuery = useEntityFeatureFlagsQuery
) => {
  return useEntityRampFlagWithLoading(
    entityName,
    rampKey,
    rampIDType,
    getEntityFlags
  ).data;
};
export const checkEntityRampFlag = async (
  entityName: string,
  rampKey: RampKey
) => {
  // Skip query if entity is anonymous
  if (isAnonymousEntity(entityName)) {
    return false;
  }

  const {data, errors} = await apolloClient.query<
    EntityFeatureFlagsQuery,
    EntityFeatureFlagsQueryVariables
  >({
    query: EntityFeatureFlagsDocument,
    fetchPolicy: 'no-cache',
    variables: {
      teamName: entityName,
      rampIDType: RampIdType.EntityName,
    },
  });
  const teamFlagsGlobalList = data?.entity?.featureFlags;
  if (!teamFlagsGlobalList || errors) {
    return false;
  }

  const isEnabled =
    teamFlagsGlobalList.find(f => f?.rampKey === rampKey)?.isEnabled ?? false;

  // This isn't ideal, but this fn is called outside the context of a React
  // hook, so useDatadogRum isn't available to us.
  getDatadogRum().addFeatureFlagEvaluation(rampKey, isEnabled);

  return isEnabled;
};

/**
 * Check a viewer-level ramp flag outside of React hooks.
 * This is useful in contexts like Redux sagas where hooks cannot be used.
 * It makes a direct GraphQL query to fetch the viewer's feature flags.
 */
export const checkViewerRampFlag = async (
  client: ApolloClient,
  rampKey: RampKey
): Promise<boolean> => {
  try {
    const {data, errors} = await client.query<
      ViewerFeatureFlagsQuery,
      ViewerFeatureFlagsQueryVariables
    >({
      query: ViewerFeatureFlagsDocument,
      fetchPolicy: 'no-cache',
      variables: {
        rampIDType: RampIdType.UserId,
      },
    });

    if (!data?.viewer?.featureFlags || errors) {
      return false;
    }

    const isEnabled =
      data.viewer.featureFlags.find(f => f?.rampKey === rampKey)?.isEnabled ??
      false;

    getDatadogRum().addFeatureFlagEvaluation(rampKey, isEnabled);

    return isEnabled;
  } catch (err) {
    console.error('Failed to check viewer ramp flag:', err);
    return false;
  }
};

export const useUserRampFlagWithLoading = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  getUserFlags: typeof useViewerFeatureFlagsQuery = useViewerFeatureFlagsQuery
) => {
  const setFlag = useDatadogFeatureFlag(rampKey);
  const {data, loading} = getUserFlags({variables: {rampIDType}});

  if (!data?.viewer?.featureFlags) {
    setFlag(false);
    // disabling this until we have an actionable use for it. It's very noisy
    // console.warn('useUserRampFlag: returning false due to no data');
    return {isEnabled: false, loading};
  }

  // note: don't default to false! undefined means not set, which is also valueable info
  const isEnabled = data.viewer.featureFlags.find(
    f => f?.rampKey === rampKey
  )?.isEnabled;

  setFlag(isEnabled);
  return {isEnabled, loading};
};

export const useUserRampFlag = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  getUserFlags: typeof useViewerFeatureFlagsQuery = useViewerFeatureFlagsQuery
) => {
  return useUserRampFlagWithLoading(rampKey, rampIDType, getUserFlags)
    .isEnabled;
};

export const useOrgRampFlagWithLoading = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  orgId: string | null = null,
  useGetOrgFlags: typeof useViewerOrganizationFeatureFlagsQuery = useViewerOrganizationFeatureFlagsQuery
) => {
  const setFlag = useDatadogFeatureFlag(rampKey);
  const {data, loading} = useGetOrgFlags({
    variables: {rampIDType},
  });

  if (loading) {
    setFlag(undefined);
    return {data: undefined, loading};
  }

  if (!data?.viewer?.organizations) {
    setFlag(undefined);
    return {data: undefined, loading};
  }

  let isOrgFlagEnabled: boolean | undefined; // undefined means not set

  // If orgId is provided and not empty, only check that specific org
  if (orgId && orgId.trim() !== '') {
    const specificOrg = data.viewer.organizations.find(org => org.id === orgId);
    if (!specificOrg) {
      setFlag(false);
      return {data: false, loading: false};
    }
    const flag = specificOrg.featureFlags?.find(f => f?.rampKey === rampKey);
    isOrgFlagEnabled = flag?.isEnabled;
    setFlag(isOrgFlagEnabled);
    return {data: isOrgFlagEnabled, loading: false};
  }

  // Otherwise check all orgs (existing behavior)
  for (const org of data.viewer.organizations) {
    const flag = org.featureFlags?.find(f => f?.rampKey === rampKey);
    isOrgFlagEnabled = flag?.isEnabled;
    if (isOrgFlagEnabled) {
      setFlag(true);
      // can exit early if enabled
      return {data: true, loading: false};
    }
  }
  setFlag(isOrgFlagEnabled);
  return {data: isOrgFlagEnabled, loading: false};
};

// This fetches all the orgs the user is a member of and returns true for a ramp flag if any
// of the orgs has the ramp flag enabled
export const useOrgRampFlag = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  getOrgFlags: typeof useViewerOrganizationFeatureFlagsQuery = useViewerOrganizationFeatureFlagsQuery
) => {
  const ret = useOrgRampFlagWithLoading(rampKey, rampIDType, null, getOrgFlags);
  return !ret.loading && ret.data;
};

// This uses the orgID (string encoded) to find the if the org's feature flag is enabled
export const useOrgIdRampFlag = (
  rampKey: RampKey,
  rampIDType: RampIdType,
  orgId: string | null = null,
  getOrgFlags: typeof useViewerOrganizationFeatureFlagsQuery = useViewerOrganizationFeatureFlagsQuery
) => {
  const ret = useOrgRampFlagWithLoading(
    rampKey,
    rampIDType,
    orgId,
    getOrgFlags
  );
  return !ret.loading && ret.data;
};

export const useOrgNameRampFlag = (
  orgName: string,
  rampKey: RampKey,
  rampIDType: RampIdType,
  getOrgFlags: typeof useOrganizationFeatureFlagsQuery = useOrganizationFeatureFlagsQuery
) => {
  const setFlag = useDatadogFeatureFlag(rampKey);
  const {data, loading} = getOrgFlags({
    variables: {orgName, rampIDType},
    skip: orgName == null,
    context: propagateErrorsContext(),
  });

  const orgFeatureFlags = data?.organization?.featureFlags;
  if (loading || orgFeatureFlags == null) {
    setFlag(false);
    return {data: false, loading};
  }
  if (data?.organization?.orgType === OrgType.Personal) {
    setFlag(false);
    return {data: false, loading};
  }

  const isEnabled =
    orgFeatureFlags.find(f => f?.rampKey === rampKey)?.isEnabled ?? false;
  setFlag(isEnabled);

  return {
    data: isEnabled,
    loading,
  };
};

/**
 * Checks viewer's user-level and org-level ramp flags for the given keys.
 * Note: currently there's no way for a user flag to take precendence over
 * an org flag, because the backend system will return a boolean for all
 * ramp keys, even ramp keys for which there is no entry that applies to
 * the user.
 */
export const useUserOrOrgRampFlag = (
  userRampKey: RampKey,
  orgRampKey: RampKey
) => {
  const userFlag = useUserRampFlag(userRampKey, RampIdType.UserName);
  const orgFlag = useOrgRampFlag(orgRampKey, RampIdType.OrgName);
  return userFlag || orgFlag || false;
};

export const useHideSummaryHistogramsRampFlag = (
  entityName: string | undefined
) => {
  return useEntityRampFlagWithLoading(
    entityName,
    'hide-summary-histograms',
    RampIdType.EntityName
  );
};
